import { useCallback, useEffect, useRef } from 'react';

import { useMapFeaturesContext } from '@/microfrontends/map/shared/context/MapLayersCenterContext';
import { type HotspotProperties } from '@/microfrontends/map/shared/types/Map';

import { boundActionsFactory } from '@/packages/redux-state/store';
import { useActivePinIdSelector } from '@/packages/redux-state/store/slices/mapControls';

import { checkIsHoverablePoint } from '../utils';

const useBoundActions = boundActionsFactory(reduxActions => ({
  setHoveredPinId: reduxActions.mapControls.setHoveredPinId,
  clearHoveredPinId: reduxActions.mapControls.clearHoveredPinId,
}));

/**
 * @description
 * Обработчики ховера на пинах
 */
export const usePointHoverHandlers = (): void => {
  const { hotspotsLayer } = useMapFeaturesContext();

  const actions = useBoundActions();

  const activePinId = useActivePinIdSelector();
  const lastActivePinIdRef = useRef(activePinId);

  const handlePointMouseEnter = useCallback(
    (event: YMaps.IEvent) => {
      const { clusterData } = event.get<YMaps.IHotspotLayerObject<HotspotProperties>>('activeObject').getProperties();

      const isHoverablePoint = checkIsHoverablePoint({
        selectedPinId: lastActivePinIdRef.current,
        currentPin: clusterData,
      });

      if (!isHoverablePoint) {
        return;
      }

      actions.setHoveredPinId(clusterData.id);
    },
    [actions],
  );

  const handlePointMouseLeave = useCallback(
    (event: YMaps.IEvent) => {
      const { clusterData } = event.get<YMaps.IHotspotLayerObject<HotspotProperties>>('activeObject').getProperties();

      const isHoverablePoint = checkIsHoverablePoint({
        selectedPinId: lastActivePinIdRef.current,
        currentPin: clusterData,
      });

      if (!isHoverablePoint) {
        return;
      }

      actions.clearHoveredPinId();
    },
    [actions],
  );

  useEffect(() => {
    hotspotsLayer.layer.events.add('mouseenter', handlePointMouseEnter);
    hotspotsLayer.layer.events.add('mouseleave', handlePointMouseLeave);

    return (): void => {
      hotspotsLayer.layer.events.remove('mouseenter', handlePointMouseEnter);
      hotspotsLayer.layer.events.remove('mouseleave', handlePointMouseLeave);
    };
  }, [hotspotsLayer, handlePointMouseEnter, handlePointMouseLeave]);

  useEffect(() => {
    lastActivePinIdRef.current = activePinId;
  }, [activePinId]);
};
